import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Search, Loader2, Scale } from "lucide-react";

export default function LegalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setSearched(true);
    try {
      const r = await api.post("/legal/search", { query });
      setResults(r.data.results || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recherche indisponible");
      if (e.response?.status === 402) navigate("/premium");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 sm:p-10 max-w-4xl">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-royal mb-2">Recherche juridique</p>
      <h1 className="font-serif text-4xl text-navy mb-6">Trouvez les textes de loi</h1>

      <div className="flex gap-2 mb-8">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="legal-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder='Ex : "licenciement économique", "préavis de départ"...'
            className="w-full pl-11 pr-4 py-3.5 border border-border rounded-md focus:ring-2 focus:ring-royal/40 focus:outline-none bg-white"
          />
        </div>
        <button data-testid="legal-search-btn" onClick={search} disabled={loading} className="bg-navy hover:bg-[#172A66] text-white px-6 rounded-md font-medium disabled:opacity-50 transition-all duration-200">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Rechercher"}
        </button>
      </div>

      {loading && (
        <div className="text-center py-16"><Loader2 className="mx-auto text-royal animate-spin mb-3" size={28} /><p className="text-slate-500">Recherche des textes pertinents...</p></div>
      )}

      {!loading && searched && results.length === 0 && (
        <p className="text-slate-400 text-center py-16">Aucun résultat. Reformulez votre recherche.</p>
      )}

      <div className="space-y-4">
        {results.map((r, i) => (
          <div key={i} data-testid={`legal-result-${i}`} className="bg-white border border-border rounded-lg p-6 hover:shadow-sm transition-all duration-200">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Scale size={16} className="text-royal" />
                <span className="font-medium text-navy">{r.code} · Article {r.article}</span>
              </div>
              {r.relevance != null && (
                <span className="text-xs bg-royal/10 text-royal px-2 py-1 rounded-full font-medium">{Math.round(r.relevance * 100)}%</span>
              )}
            </div>
            {r.titre && <p className="font-serif text-lg text-navy mb-2">{r.titre}</p>}
            {r.texte && <p className="text-sm text-slate-500 italic mb-3 font-mono">{r.texte}</p>}
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1">En langage simple</p>
            <p className="text-sm text-slate-700 leading-relaxed">{r.explication}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
